import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, X, Pencil, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABEL, formatDateTime } from "@/lib/format";
import AdminUserEditDrawer from "@/components/admin/AdminUserEditDrawer";
import AdminDriverEditDrawer from "@/components/admin/AdminDriverEditDrawer";

const FILTERS = [
  { id: "all",                label: "Tutti" },
  { id: "customers",          label: "Clienti" },      // privati + aziende + negozi
  { id: "customer_private",   label: "Privati" },
  { id: "customer_business",  label: "Aziende" },
  { id: "customer_shop",      label: "Negozi" },
  { id: "driver",             label: "Trasportatori" },
  { id: "admin",              label: "Admin" },
];

const CUSTOMER_ROLES = new Set(["customer_private", "customer_business", "customer_shop"]);

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  // Phase 44.31.10 — supporta deep link da sidebar ?role=customers (group filter)
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get("role") || "all";
  const [filter, setFilter] = useState(FILTERS.some((f) => f.id === initialFilter) ? initialFilter : "all");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editDriver, setEditDriver] = useState(null);
  // Phase 44.31.18 — toggle "Includi archiviati" (default OFF)
  const [includeArchived, setIncludeArchived] = useState(searchParams.get("archived") === "1");

  const load = async () => {
    setLoading(true);
    // Backend non supporta multi-role: per "customers" carica tutto e filtra client-side
    const params = (filter === "all" || filter === "customers") ? {} : { role: filter };
    if (includeArchived) params.include_archived = "true";
    try {
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, includeArchived]);

  // Phase 44.31.10 — Reagisce a cambi esterni del query param ?role=
  useEffect(() => {
    const r = searchParams.get("role") || "all";
    if (FILTERS.some((f) => f.id === r) && r !== filter) setFilter(r);
    const q = searchParams.get("q") || "";
    if (q !== search) setSearch(q);
    // eslint-disable-next-line
  }, [searchParams]);

  // Sync filter → URL ?role=…
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    if (filter && filter !== "all") sp.set("role", filter);
    else sp.delete("role");
    if (search) sp.set("q", search); else sp.delete("q");
    if (includeArchived) sp.set("archived", "1"); else sp.delete("archived");
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line
  }, [filter, search, includeArchived]);

  const filtered = useMemo(() => {
    let base = users;
    if (filter === "customers") base = users.filter((u) => CUSTOMER_ROLES.has(u.role));
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.phone || "").toLowerCase().includes(q) ||
      (u.business_name || "").toLowerCase().includes(q) ||
      (u.business_vat || "").toLowerCase().includes(q) ||
      (u.customer_number || "").toLowerCase().includes(q) ||
      (u.driver_number || "").toLowerCase().includes(q),
    );
  }, [users, search, filter]);

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div>
        <p className="overline">Community</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Utenti</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-dapnow-muted" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, email, telefono, P.IVA, numero cliente/driver…"
            data-testid="admin-users-search"
            className="input-tall w-full pl-10 pr-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="admin-users-search-clear"
              aria-label="Pulisci"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-dapnow-bg"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`admin-users-filter-${f.id}`}
              className={`shrink-0 rounded-full px-4 h-10 text-sm font-semibold transition ${
                filter === f.id ? "bg-dapnow-ink text-white" : "bg-white border border-dapnow-line text-dapnow-ink hover:border-dapnow-blue/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {/* Phase 44.31.18 — Toggle "Includi archiviati" */}
        <label className="inline-flex items-center gap-2 text-xs text-dapnow-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="admin-users-include-archived"
            className="h-4 w-4 rounded border-dapnow-line accent-dapnow-blue"
          />
          Includi archiviati
        </label>
      </div>

      {!loading && (
        <p className="text-xs text-dapnow-muted" data-testid="admin-users-count">
          {filtered.length} utenti{search ? ` (filtrati da ${users.length})` : ""}
        </p>
      )}

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: "1000px" }}>
            <thead className="bg-dapnow-bg text-left">
              <tr>
                <Th>N°</Th>
                <Th>Nome</Th>
                <Th>Email</Th>
                <Th>Ruolo</Th>
                <Th>Telefono</Th>
                <Th>Iscritto</Th>
                <Th className="text-right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="p-10 text-center text-dapnow-muted">Caricamento…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="7" className="p-10 text-center text-dapnow-muted">
                  {search ? "Nessun utente trovato per la ricerca." : "Nessun utente."}
                </td></tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id} className={`border-t border-dapnow-line ${u.archived ? "opacity-60 bg-zinc-50" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                  <Td className="font-mono text-xs text-dapnow-muted whitespace-nowrap">{u.driver_number || u.customer_number || "—"}</Td>
                  <Td className="font-semibold">
                    {u.name}{u.business_name ? <span className="text-xs text-dapnow-muted block">{u.business_name}</span> : null}
                    {u.archived && (
                      <span
                        className="ml-1.5 inline-flex items-center pill bg-zinc-200 text-zinc-700 text-[9px] uppercase tracking-wider"
                        title={`${u.archived_reason || "archiviato"} · ${u.archived_at ? formatDateTime(u.archived_at) : ""}${u.archived_batch ? " · " + u.archived_batch : ""}`}
                        data-testid={`admin-user-archived-badge-${u.id}`}
                      >
                        Archiviato
                      </span>
                    )}
                  </Td>
                  <Td>{u.email}</Td>
                  <Td><span className="pill bg-dapnow-blue-soft text-dapnow-blue">{ROLE_LABEL[u.role]}</span></Td>
                  <Td>{u.phone || "—"}</Td>
                  <Td className="text-xs text-dapnow-muted">{formatDateTime(u.created_at)}</Td>
                  <Td className="text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {u.role === "driver" && (
                        <Link
                          to={`/admin/drivers/${u.id}`}
                          data-testid={`admin-user-detail-${u.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-dapnow-line bg-white px-2.5 h-8 text-xs font-semibold hover:border-dapnow-blue hover:text-dapnow-blue"
                          title="Apri scheda completa"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Scheda
                        </Link>
                      )}
                      {["customer_private", "customer_business", "customer_shop"].includes(u.role) && (
                        <Link
                          to={`/admin/customers/${u.id}`}
                          data-testid={`admin-user-detail-${u.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-dapnow-line bg-white px-2.5 h-8 text-xs font-semibold hover:border-dapnow-blue hover:text-dapnow-blue"
                          title="Apri scheda cliente"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Scheda
                        </Link>
                      )}
                      {["customer_private", "customer_business", "customer_shop"].includes(u.role) ? (
                        <button
                          onClick={() => setEditUser(u)}
                          data-testid={`admin-user-edit-${u.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-dapnow-line bg-white px-2.5 h-8 text-xs font-semibold hover:border-dapnow-blue hover:text-dapnow-blue"
                          title="Modifica cliente"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modifica
                        </button>
                      ) : u.role === "driver" ? (
                        <button
                          onClick={() => setEditDriver(u)}
                          data-testid={`admin-user-edit-${u.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-dapnow-line bg-white px-2.5 h-8 text-xs font-semibold hover:border-dapnow-blue hover:text-dapnow-blue"
                          title="Modifica trasportatore"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modifica
                        </button>
                      ) : (
                        <span className="text-xs text-dapnow-muted">—</span>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AdminUserEditDrawer
        user={editUser}
        onClose={() => setEditUser(null)}
        onSaved={() => { setEditUser(null); load(); }}
      />

      {editDriver && (
        <AdminDriverEditDrawer
          driver={editDriver}
          onClose={() => setEditDriver(null)}
          onSaved={() => { setEditDriver(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 overline text-[10px] font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
